import {Box, Pallet} from '@/generated/model/models'
import * as THREE from 'three'
import { basePath } from '@/main'
import { ProgramSettings, BoxPosition, Sequence } from '@/model'
import { Box2 } from '@/libs/box2'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { calculateCustomLabelPosition, findSequenceByBoxId, getCustomInfeedRotation } from '@/libs/pattern/sequenceUtils'

const defWidth = 530
// const defHeight = 1500
const LONG_SIDE_COLOR = 0xee99a4 // 0xf14e62 // 0xee99a4 // 0xf14e62 // 0xBA0C2F // 0xffaa99
const SHOT_SIDE_COLOR = 0x399eee // 0x005eb8 // 0x88aaff

export interface ViewPort {
  h: number,
  w: number
}

export class Pallet3D {
  viewPort: ViewPort = { h: 700, w: 700}
  private background: number = 0xfafafa
  private box: Box2
  private pallet: Pallet
  private layerSpace: number = 330
  private programSettings: ProgramSettings
  private labelPos: ProgramSettings.LabelPositionEnum

  private container: HTMLElement
  private scene: THREE.Scene
  private camera: THREE.OrthographicCamera
  private renderer: THREE.WebGLRenderer
  private activeLayer: number = 0
  private boxMaterials: THREE.Material[]
  private layersGroups: THREE.Group[] = []
  private palletGroup?: THREE.Group
  private intersheets: THREE.Object3D[] = []
  private wide: boolean = false
  private group: THREE.Group
  private controls: OrbitControls

  constructor (elementID: string, box: Box, pallet: Pallet, programSettings: ProgramSettings,
               background?: number, viewPort?: ViewPort, layerSpace?: number) {
    this.background = background ? background : this.background
    this.pallet = pallet
    this.viewPort = viewPort ? viewPort : this.viewPort
    this.layerSpace = (layerSpace != null) ? layerSpace : this.layerSpace
    this.programSettings = programSettings
    this.labelPos = programSettings.labelPosition!
    this.box = new Box2(box)
    if (programSettings.boxOrientationOnConveyor === ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING) {
      this.box.setShortSideLeading()
    } else {
      this.box.setLongSideLeading()
    }


    let element = document.getElementById(elementID)
    if (!element) {
      console.log(`[ERROR] html element '${elementID}' not found`)
      element = document.createElement(elementID)
    } else {
      // clean element content
      while (element.firstChild) {
        element.firstChild.remove()
      }
    }
    const child = document.createElement(elementID + '_container')
    element.appendChild(child)

    this.container = child
    this.scene = this.sceneSetup()
    this.camera = this.cameraSetup()
    this.lightSetup()

    this.group = new THREE.Group()
    this.group.position.set(0, 0, 0)
    this.scene.add(this.group)

    this.boxMaterials = this.boxSetup()
    this.renderer = this.renderSetup()
    this.controls = this.controlsSetup()
    this.palletGroup = this.makePallet(this.pallet)
    if (this.isCustomSolution()) {
      this.addReferenceFrame()
    }
    this.renderer.render(this.scene, this.camera)
  }

  sceneSetup (): THREE.Scene {
    const scene = new THREE.Scene()

    // invert coordinate system !!!
    const m = new THREE.Matrix4().fromArray([1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0.8, 0, 0, 0, 0, 1])
    scene.applyMatrix(m)
    scene.updateMatrix()

    scene.background = new THREE.Color(this.background)
    scene.add(new THREE.AmbientLight(0xffffff))
    return scene
  }

  cameraSetup (): THREE.OrthographicCamera {
    const aspect = this.viewPort.w / this.viewPort.h
    // create an orthographic view with 1500 mm radius
    const frustrumRadius = 1500
    const camera = new THREE.OrthographicCamera(-frustrumRadius * aspect, frustrumRadius * aspect, frustrumRadius, -frustrumRadius, 0, 5000)
    // make z axis up
    camera.up = new THREE.Vector3(0, 0, 1)
    // simulate the viewpoint of a 180 cm tall person stepping back the length of a pallet in both x and y direction
    const pos = new THREE.Vector3(-this.pallet.length!, -this.pallet.width! - this.pallet.length!, 1800)
    camera.position.set(pos.x, pos.y, pos.z)
    // angle the camera towards the center position of the pallet 90 cm above ground
    const look = new THREE.Vector3(this.pallet.length! / 2, -this.pallet.width! / 2, 900)
    camera.lookAt(look)
    this.camera = camera // extra init for method
    return camera
  }

  lightSetup () {
    const light = new THREE.SpotLight(0xffffff, 0.7)
    light.position.set(-1000, -5000, 5000)
    // noinspection PointlessArithmeticExpressionJS
    light.angle = Math.PI / 1
    light.castShadow = true
    light.shadow.camera.near = 2000
    light.shadow.camera.far = 5000
    light.shadow.mapSize.width = 1024
    light.shadow.mapSize.height = 1024

    const light2 = new THREE.AmbientLight(0xf0f0f0)
    this.scene.add(light, light2)
  }

  renderSetup (): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(this.viewPort.w, this.viewPort.h)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFShadowMap
    this.container.appendChild(renderer.domElement)
    renderer.render(this.scene, this.camera)
    return renderer
  }

  controlsSetup(): OrbitControls {
    const controls = new OrbitControls(this.camera, this.renderer.domElement)
    controls.addEventListener('change', () => { this.rerender() })
    controls.enableDamping = false
    controls.dampingFactor = 0.25
    controls.screenSpacePanning = false
    controls.minDistance = 100
    controls.maxDistance = 10000
    controls.maxPolarAngle = Math.PI / 2
    return controls
  }

  resize (height: number) {
    this.viewPort.h = height
  }

  boxSetup (): THREE.Material[] {
    // const boxColorTexture = new THREE.TextureLoader()
    //   .load(`${basePath}img/box-white.jpg`, () => this.renderer.render(this.scene, this.camera))

    const boxTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box.jpg`, () => this.renderer.render(this.scene, this.camera))

    const boxTopTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-top.jpg`, () => this.renderer.render(this.scene, this.camera))

    // noinspection JSUnusedLocalSymbols
    const boxLabeledTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-labeled.jpg`, () => this.renderer.render(this.scene, this.camera))

    const emptySide = new THREE.MeshStandardMaterial({ map: boxTexture, roughness: 1 })
    const longSide = new THREE.MeshStandardMaterial({ color: LONG_SIDE_COLOR, map: boxTexture, roughness: 1 })
    const shortSide = new THREE.MeshStandardMaterial({ color: SHOT_SIDE_COLOR, map: boxTexture, roughness: 1 })
    const topSide = new THREE.MeshStandardMaterial({ map: boxTopTexture, roughness: 0.9 })

    return [ shortSide, shortSide, longSide, longSide, topSide, emptySide ]
  }

  makePallet (pallet: Pallet): THREE.Group {
    if (this.palletGroup) {
      this.group.remove(this.palletGroup)
    }
    this.pallet.height = this.pallet.height ? this.pallet.height : 125
    const color = 0xc1af7a
    const p = { l: pallet.length!, w: 100, h: 20 }
    const b = { l: pallet.width!, w: pallet.height! - p.h, h: pallet.height! - p.h }

    const beam = new THREE.BoxBufferGeometry(b.l, b.w, b.h)
    const plank = new THREE.BoxBufferGeometry(p.l, p.w, p.h)

    const plankTexture = new THREE.TextureLoader()
      .load(`${basePath}img/pine.jpg`, () => this.renderer.render(this.scene, this.camera))

    const material = new THREE.MeshStandardMaterial({ map: plankTexture, roughness: 1 })

    const beam1 = new THREE.Mesh(beam, material)
    beam1.rotateZ(90 / 180 * Math.PI)
    beam1.position.x = b.w / 2
    beam1.position.y = b.l / 2
    beam1.position.z = b.h / 2

    const beam2 = new THREE.Mesh(beam, material)
    beam2.rotateZ(90 / 180 * Math.PI)
    beam2.position.x = pallet.length! / 2
    beam2.position.y = b.l / 2
    beam2.position.z = b.h / 2

    const beam3 = new THREE.Mesh(beam, material)
    beam3.rotateZ(90 / 180 * Math.PI)
    beam3.position.x = pallet.length! - b.w / 2
    beam3.position.y = b.l / 2
    beam3.position.z = b.h / 2

    const plank1 = new THREE.Mesh(plank, material)
    plank1.position.x = p.l / 2
    // noinspection PointlessArithmeticExpressionJS
    plank1.position.y = (pallet.width! - p.w) / 4 * 0 + p.w / 2
    plank1.position.z = b.h + p.h / 2

    const plank2 = new THREE.Mesh(plank, material)
    plank2.position.x = p.l / 2
    // noinspection PointlessArithmeticExpressionJS
    plank2.position.y = (pallet.width! - p.w) / 4 * 1 + p.w / 2
    plank2.position.z = b.h + p.h / 2

    const plank3 = new THREE.Mesh(plank, material)
    plank3.position.x = p.l / 2
    plank3.position.y = (pallet.width! - p.w) / 4 * 2 + p.w / 2
    plank3.position.z = b.h + p.h / 2

    const plank4 = new THREE.Mesh(plank, material)
    plank4.position.x = p.l / 2
    plank4.position.y = (pallet.width! - p.w) / 4 * 3 + p.w / 2
    plank4.position.z = b.h + p.h / 2

    const plank5 = new THREE.Mesh(plank, material)
    plank5.position.x = p.l / 2
    plank5.position.y = (pallet.width! - p.w) / 4 * 4 + p.w / 2
    plank5.position.z = b.h + p.h / 2

    const group = new THREE.Group()
    group.add(beam1, beam2, beam3, plank1, plank2, plank3, plank4, plank5)
    this.group.add(group)
    return group
  }

  addBox (x: number, y: number, z: number, rot: number, labelPosition?: ProgramSettings.LabelPositionEnum): THREE.Group {
    const boxGroup = new THREE.Group()

    /*
    const width = this.programSettings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING' ? this.box.length
      : this.box.width
    const height = this.programSettings.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING' ? this.box.width
      : this.box.length
    */

    const geometry = new THREE.BoxBufferGeometry(this.box.sideLength, this.box.frontLength, this.box.height)
    const boxObject = new THREE.Mesh(geometry, this.boxMaterials)
    boxGroup.add(boxObject)
    this.setLabel(labelPosition || this.programSettings.labelPosition!, boxGroup)
    boxGroup.position.x = this.getBoxX(x)
    boxGroup.position.y = y
    boxGroup.position.z = z
    boxGroup.rotation.x = 0
    boxGroup.rotation.y = 0
    // convert from pallet coordinate system with anti-clockwise rotation to three.js rotation (clockwise radians)
    boxGroup.rotation.z = -rot / 180 * Math.PI
    boxGroup.scale.x = 1
    boxGroup.scale.y = 1
    boxGroup.scale.z = 1
    boxGroup.castShadow = true
    boxGroup.receiveShadow = true
    this.group.add(boxGroup)
    return boxGroup
  }

  addLayer (pattern: BoxPosition[], sequences?: Sequence[]) {
    if (!pattern || pattern.length! === 0) {
      // default layout for test purpose
      // pattern = this.makePattern(this.pallet.length!, this.pallet.width!, this.box, 0, this.pallet)
    }
    const group = new THREE.Group()
    const h = this.getLayerZ(this.activeLayer)
    pattern.forEach(point => {
      const box = this.addBox(point.x!, point.y!, h, point.rotation!, this.getLabelPosition(point, sequences))
      group.add(box)
    })
    this.layersGroups.push(group)
    this.group.add(group)
    this.activeLayer += 1
    if (this.renderer) {
      this.renderer.render(this.scene, this.camera)
    }
  }

  removeLayer () {
    const toRemove: THREE.Object3D[] = []
    const layerObjects = this.layersGroups.pop()
    if (layerObjects) {
      toRemove.push(layerObjects)
    }
    if (this.intersheets.length > 0) {
      const intersheet = this.intersheets.pop()
      if (intersheet) {
        toRemove.push(intersheet)
      }
    }
    toRemove.forEach((p: THREE.Object3D) => {
        this.group.remove(p)
      }
    )
    this.activeLayer = this.activeLayer === 0 ? 0 : this.activeLayer - 1
    this.renderer.render(this.scene, this.camera)
  }

  batchLayers (wide: boolean) {
    this.wide = wide

    // clean
    this.group.remove(...this.layersGroups, ...this.intersheets)
    this.intersheets = []
    this.layersGroups = []

    this.activeLayer = 0
    this.pallet.layers!.forEach(layer => {
      const pattern = this.pallet.layerPatterns!.find(p => p.name === layer.layerPattern)
      if (pattern && pattern.boxPositions) {
        this.addLayer(pattern.boxPositions, pattern.sequences)
      }
    })
    if (this.pallet.interlayerSheetPositions) {
      this.pallet.interlayerSheetPositions.forEach((p, i) => {
        if (p) {
          this.addInterSheetByLayer(i)
        }
      })
    }
  }

  addHelpers () {
    // base surface
    const base = new THREE.PlaneGeometry(this.pallet.length, this.pallet.width)
    const obj = new THREE.Mesh(base, new THREE.MeshLambertMaterial({ color: 0x888888 }))
    obj.position.x = this.pallet.length! / 2
    obj.position.y = this.pallet.width! / 2
    // this.scene.add(obj)

    // axes
    const axesHelper = new THREE.AxesHelper(2000)
    this.scene.add(axesHelper)

    // background
    this.scene.background = new THREE.Color(0xccfafa)
  }

  rerender () {
    this.renderer.render(this.scene, this.camera)
  }

  addInterSheetByLayer (index: number) {
    const sheet = this.newIntersheet()
    sheet.position.z = this.getInterSheetPosZ(index)
    this.group.add(sheet)
    this.intersheets.push(sheet)
  }

  removeInterSheetByLayer (index: number) {
    const h = this.getInterSheetPosZ(index)
    const found = this.intersheets.findIndex(p => p.position.z === h)
    // console.log('[DEBUG] found', found)
    if (found > -1) {
      this.group.remove(this.intersheets[found])
      this.intersheets.splice(found, 1)
    }
  }

  private addReferenceFrame () {
    const origin = new THREE.Vector3(this.pallet.length!, 0, this.pallet.height! + 8)
    const axisLength = Math.min(this.pallet.length!, this.pallet.width!) * 0.35
    const color = 0xff0000
    const material = new THREE.LineBasicMaterial({ color })
    const xAxis = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
      origin,
      new THREE.Vector3(origin.x - axisLength, origin.y, origin.z)
    ]), material)
    const yAxis = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
      origin,
      new THREE.Vector3(origin.x, origin.y + axisLength, origin.z)
    ]), material)
    const xArrow = new THREE.ArrowHelper(new THREE.Vector3(-1, 0, 0), origin, axisLength, color, axisLength * 0.18, axisLength * 0.08)
    const yArrow = new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), origin, axisLength, color, axisLength * 0.18, axisLength * 0.08)
    const originMarker = new THREE.Mesh(
      new THREE.SphereGeometry(axisLength * 0.035, 16, 16),
      new THREE.MeshBasicMaterial({ color })
    )
    originMarker.position.copy(origin)
    this.group.add(xAxis, yAxis, xArrow, yArrow, originMarker)
  }

  private getLayerZ (index: number): number {
    const space = this.wide ? this.layerSpace : 0
    return this.pallet.height! + (this.box.height! + space) * index + this.box.height! / 2
  }

  private getInterSheetPosZ (index: number): number {
    let h = 0
    if (this.layersGroups.length === 0) {
      return this.pallet.height!
    }
    if (index < this.layersGroups.length) {
      h = this.getLayerZ(index) - this.box.height! / 2
    } else {
      h = this.getLayerZ(index - 1) + this.box.height! / 2 + 50
    }
    return h
  }

  private getLabelPosition (point: BoxPosition, sequences?: Sequence[]): ProgramSettings.LabelPositionEnum {
    const labelPosition = this.programSettings.labelPosition!
    if (this.programSettings.robotType !== 'CUSTOM_SOLUTION') {
      return labelPosition
    }
    if (!sequences || sequences.length === 0 || point.id == null) {
      return ProgramSettings.LabelPositionEnum.IGNORE
    }
    const sequence = findSequenceByBoxId(point.id, sequences)
    if (!sequence) {
      return ProgramSettings.LabelPositionEnum.IGNORE
    }
    const pick = sequence.pick!.find(p => (p.boxesGroup || []).includes(point.id!))
    const pickRotation = pick && pick.zRot != null ? pick.zRot : 0
    const placeRotation = sequence.place && sequence.place[0] && sequence.place[0].zRot != null ? sequence.place[0].zRot : 0
    const boxRotation = point.rotation || 0
    return calculateCustomLabelPosition(
      labelPosition,
      this.programSettings.boxOrientationOnConveyor,
      this.programSettings.conveyorConfiguration,
      boxRotation,
      pickRotation,
      placeRotation,
      getCustomInfeedRotation(sequence)
    )
  }

  private getBoxX (x: number): number {
    return this.isCustomSolution() ? this.pallet.length! - x : x
  }

  private isCustomSolution (): boolean {
    return this.programSettings.robotType === 'CUSTOM_SOLUTION'
  }

  // noinspection JSMethodCanBeStatic
  private makeLabel (): THREE.Group {
    const L = 100
    const W = 45
    const geometry = new THREE.PlaneGeometry(L, W)
    const paper = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xeeeeee }))
    const group = new THREE.Group()
    group.add(paper)
    group.position.x = L / 2
    group.position.y = W / 2
    group.position.z = 200
    group.name = 'label'
    return group
  }

  private setLabel (pos: ProgramSettings.LabelPositionEnum, boxGroup: THREE.Group) {
    const labelA = this.makeLabel()
    const labelB = this.makeLabel()
    this.labelPos = pos
    const length = 100
    const frontLength = this.box.frontLength
    const sideLength = this.box.sideLength

    const front = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = -Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = -(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const back = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = +(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const right = (label: THREE.Object3D) => {
      label.rotation.x = Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = -(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const left = (label: THREE.Object3D) => {
      label.rotation.x = -Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = +(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    switch (pos) {
      case ProgramSettings.LabelPositionEnum.FRONT:
        // boxGroup.remove(labelB)
        boxGroup.add(labelA)
        front(labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT:
        // boxGroup.remove(labelB)
        boxGroup.add(labelA)
        right(labelA)
        break

      case ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        right(labelA)
        front(labelB)
        labelA.position.x = -(sideLength / 2 - length / 2)
        labelB.position.y = -(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        left(labelA)
        break

      case ProgramSettings.LabelPositionEnum.BACK:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        back(labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT_BACK_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        right(labelA)
        back(labelB)
        labelA.position.x = +(sideLength / 2 - length / 2)
        labelB.position.y = -(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        left(labelA)
        front(labelB)
        labelA.position.x = -(sideLength / 2 - length / 2)
        labelB.position.y = +(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.BACK_LEFT_CORNER:
        // boxGroup.remove(this.labelB)
        boxGroup.add(labelA)
        boxGroup.add(labelB)
        left(labelA)
        back(labelB)
        labelA.position.x = +(sideLength / 2 - length / 2)
        labelB.position.y = +(frontLength / 2 - length / 2)
        break
    }
    // this.render()
  }

  private newIntersheet(): THREE.Group {
    const marginL = this.pallet.length! * 0.08
    const marginW = this.pallet.length! * 0.08
    const color = 0x89785f // 0xff8888
    const L = this.pallet.length! + marginL
    const W = this.pallet.width! + marginW

    const geometry = new THREE.PlaneGeometry(L, W)
    const object = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial(
      { color: color, transparent: true, opacity: 0.5, side: THREE.DoubleSide, roughness: 1 }
    ))
    object.castShadow = true
    object.receiveShadow = true

    const edges = new THREE.EdgesGeometry(geometry)
    const line = new THREE.LineSegments(edges, new THREE.LineDashedMaterial({ color: 0x999999, dashSize: 100, gapSize: 0 }))
    line.computeLineDistances()
    const group = new THREE.Group()
    group.add(object, line)

    group.position.x = (L - marginL) / 2
    group.position.y = (W - marginW) / 2
    group.position.z = -10000 // z
    group.rotation.x = 0
    group.rotation.y = 0
    group.rotation.z = 0
    group.scale.x = 1
    group.scale.y = 1
    group.scale.z = 1
    return group
  }
}
